import React, {
  useState,
  Fragment,
  useRef,
  useEffect,
  useCallback,
  memo,
} from 'react';
import styled from 'styled-components';
import { Title2 } from '../../../components/Typography';
import {
  ORDER_REFUNDED,
  ORDER_REJECTED,
  ORDER_SUBMITTED,
  ORDER_ACCEPTED,
  ORDER_COMPLETE,
  ORDER_ON_DELIVERY,
  ORDER_READY_TO_PICKUP,
} from '../orderStatusTypes';
import OrderStatusModal3 from './OrderStatusModal3';
import { FlatButton } from '../../../components/Buttons';
import FeedbackModal from './FeedbackModal';
import Icon from './Icon';
import Slider from 'react-slick';
import appConfig from '../../../../app-config';
import {
  DELIVERY,
  PICKUP,
  DROPOFF,
} from '../../OrderSetting/orderSettingTypes';
import moment from 'moment';
import TrackButton from './TrackButton';
import calculateOrderReadyTime from '../utils/calculateOrderReadyTime';

const NextArrow = styled.div`
  @media (max-width: 1100px) {
    right: 10px !important;
  }
  @media (orientation: landscape) and (max-width: 1100px) {
    right: -20px !important;
  }
  @media (max-width: 786px) {
    display: none !important;
  }
`;

const PrevArrow = styled.div`
  @media (max-width: 786px) {
    display: none !important;
  }
`;

const Separator = styled.div`
  width: 3em;
  height: 4px;
  border-radius: 2px;
  background-color: #b4c3db;
  margin: 0px 1em 0px 1em;
`;

const OrderStatusContainer = styled.div`
  width: 532px;
  max-height: 400px;
  @media only screen and (min-width: 768px) {
    width: 450px;
  }
  @media (max-width: 480px) {
    max-width: 340px;
    max-height: 410px;
  }
`;
const OrderStatusWrapper = styled.div`
  width: 532px;
  height: 266px;
  padding: 16px 24px 52px;
  background: ${(props) => props.theme.orderStatusBackgroundColor};
  border-radius: 10px;
  cursor: ${(props) => !props.isForModal && 'pointer'};
  justify-content: center;
  &:hover {
    background: ${(props) =>
      !props.isForModal && props.theme.orderStatusBackgroundColorHover};
    transition: all 0.25s ease;
  }
  @media only screen and (min-width: 768px) {
    width: 450px;
    margin: 15px 0;
  }
  @media (max-width: 480px) {
    max-width: 340px;
    margin: 15px 0;
    height: 220px;
  }
`;
const FeedbackWrapper = styled.div`
  width: 100%;
  border-radius: 12px;
  padding: 12px 16px;
  background: #f7f7f7;
  grid-gap: 12px;
  display: grid;
  grid-template-columns: 0.7fr 0.3fr;
  margin: 15px 0;
  @media only screen and (min-width: 768px) {
    width: 450px;
  }
  @media (max-width: 480px) {
    display: block;
    max-width: 340px;
    margin-bottom: 20px;
  }
`;
const RateTextWrapper = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr;
`;
const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: center;
  @media (max-width: 480px) {
    margin-top: 10px;
  }
`;
const Wrapper = styled.div`
  margin: 32px auto 0 0;
  max-width: 480px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-wrap: wrap;
  @media (max-width: 479px) {
    padding: 5px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: ${(props) => (props.mini ? '4em' : '7em')};

  @media (max-width: 480px) {
    height: 5em;
  }
`;

const Title = styled(Title2)`
  color: ${(props) => props.theme.orderStatusTitleColor};
  margin-bottom: 10px;
`;
const TitleNote = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.02px;
  text-align: right;
  color: #737a87;
`;
const TitleWrapper = styled.div`
  display: grid;
  grid-template-columns: 0.7fr 0.3fr;
`;

const Button = styled(FlatButton)`
  border-radius: 17.5px;
  padding: 7px 16px;
  background: ${(props) => props.theme.primaryButtonBackgroundColor};
  color: ${(props) => props.theme.primaryButtonFontColor};

  &:hover {
    background: ${(props) => props.theme.primaryButtonHoverColor};
    transition: all 0.25s ease;
  }
`;

const OrderStatusMessage = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  font-family: ${(props) => props.font || ''};
  color: ${(props) => props.customColor || 'black'};
`;

const OrderStatusSubMessage = styled.div`
  margin: 6px 0 0;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.02px;
  font-family: ${(props) => props.font || ''};
  color: ${(props) => props.customColor || '#737a87'};
`;
const BarContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
  height: 8px;
  position: relative;
  overflow: hidden;
  align-items: left;
`;

const ProgressBar = styled.div`
  height: 10px;
  margin-left: 5px;
  border-right: 10px solid ${(props) => props.theme.orderStatusBackgroundColor};
  width: 100%;

  ${(props) =>
    props.animate
      ? `
  animation: load 4s infinite linear;
  background: repeating-linear-gradient(
      45deg,
      transparent 0 6px,
      rgba(0, 0, 0, 0.1) 6px 12px
    ),
    linear-gradient(#b0d0ff, #2e82ff);
  background-size: calc(12px / 0.707) 100%, 100% 800%; /*cos(45deg) == 0.707*/
  background-position: inherit;

  @keyframes load {
    0% {
      background-position: 0 0;
    }
    100% {
      background-position: calc(10 * (12px / 0.707)) 100%;
    }
  }
  `
      : `background: #b4c3d8;`}
`;
const CardContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  align-items: center;
  margin-top: 3.5em;
  height: ${(props) => (props.mini ? '4em' : '5em')};
`;
const OrderPlacedTimeWrapper = styled.div`
  font-family: 'Lato', sans-serif;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  padding-left: 5px;
`;
const isOrderWithFirebaseUrl = (orderHistory, orderStatus) => {
  const orderHist = orderHistory.filter(
    (order) => order.order_id == orderStatus.orderId
  );

  if (orderHist[0]?.firebase_uri && orderHist[0]?.firebase_uri?.length !== 0) {
    return true;
  } else {
    return false;
  }
};
const SlideWrap = styled.div`
  margin-top: 10px;
  .slick-slide {
    padding: 0;
    margin-right: 10px;
  }
  .slick-list {
    margin: 0;
  }
  @media (min-width: 1800px) {
    .slick-list {
      min-width: 1600px !important;
    }
  }
  @media (min-width: 1550px) {
    .slick-list {
      min-width: 1470px !important;
    }
  }
  @media (min-width: 1400px) {
    .slick-list {
      min-width: 1170px !important;
    }
  }
  @media (orientation: landscape) and (max-width: 1100px) {
    .slick-list {
      width: 1255px !important;
    }
  }
`;
function OrderHistoryNextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <NextArrow
      className={className}
      style={{
        ...style,
        display: 'block',
        background: '#fff',
        borderRadius: '50%',
        border: 'none',
        height: '40px',
        width: '40px',
        boxShadow: ' 0 2px 5px 0 rgba(59, 76, 112, 0.24)',
        top: props.feedbackRef.current ? '35%' : '45%',
        // right: '-10px',
        opacity: 1,
        fontSize: '16px',
        lineHeight: '40px',
        textAlign: 'center',
      }}
      onClick={onClick}
    />
  );
}

function OrderHistoryPrevArrow(props) {
  const { className, style, onClick } = props;
  return (
    <PrevArrow
      className={className}
      style={{
        ...style,
        display: 'block',
        background: '#fff',
        borderRadius: '50%',
        border: 'none',
        height: '40px',
        width: '40px',
        boxShadow: ' 0 2px 5px 0 rgba(59, 76, 112, 0.24)',
        top: props.feedbackRef.current ? '35%' : '45%',
        // left: '-20px',
        opacity: 1,
        lineHeight: '40px',
        textAlign: 'center',
        zIndex: '1',
      }}
      onClick={onClick}
    />
  );
}
const slideSettings = (feedbackRef) => {
  return {
    dots: false,
    infinite: false,
    speed: 600,
    arrows: true,
    slidesToShow: 2,
    slidesToScroll: 1,
    initialSlide: 0,
    swipeToSlide: true,
    variableWidth: true,
    nextArrow: <OrderHistoryNextArrow feedbackRef={feedbackRef} />,
    prevArrow: <OrderHistoryPrevArrow feedbackRef={feedbackRef} />,
    rows: 1,
    responsive: [
      {
        breakpoint: 1600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          rows: 1,
          nextArrow: <OrderHistoryNextArrow feedbackRef={feedbackRef} />,
          prevArrow: <OrderHistoryPrevArrow feedbackRef={feedbackRef} />,
        },
      },
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 1.7,
          slidesToScroll: 1,
          rows: 1,
          nextArrow: <OrderHistoryNextArrow feedbackRef={feedbackRef} />,
          prevArrow: <OrderHistoryPrevArrow feedbackRef={feedbackRef} />,
        },
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          rows: 1,
          nextArrow: <OrderHistoryNextArrow feedbackRef={feedbackRef} />,
          prevArrow: <OrderHistoryPrevArrow feedbackRef={feedbackRef} />,
        },
      },
    ],
  };
};
const SliderWrapper = ({ isMultiple, feedbackRef, children }) => {
  if (!isMultiple) {
    return <Wrapper>{children}</Wrapper>;
  }
  return (
    <SlideWrap>
      <Slider {...slideSettings(feedbackRef)}>{children}</Slider>
    </SlideWrap>
  );
};
const RenderOrderCompleteIcon = ({ orderType, status }) => {
  if (orderType === PICKUP)
    return (
      <>
        {status === ORDER_READY_TO_PICKUP ? (
          <Icon className="fas fa-store" />
        ) : (
          <Icon className="fal fa-store" />
        )}
      </>
    );

  if (orderType === DELIVERY)
    return (
      <>
        {status === ORDER_ON_DELIVERY ? (
          <Icon className="fas fa-car-side" />
        ) : (
          <Icon className="fal fa-car-side" />
        )}
      </>
    );

  if (orderType === DROPOFF)
    return (
      <>
        {status === ORDER_COMPLETE ? (
          <Icon className="fas fa-shopping-bag" />
        ) : (
          <Icon className="fal fa-shopping-bag" />
        )}
      </>
    );
};
const OrderStatusBody = ({
  orderType,
  status,
  businessName,
  orderReadyTime,
  deliveryTrackingURL,
  delay,
  index,
  timeList,
  statusMessage,
}) => {
  return (
    <ContentWrapper>
      <CardContent>
        {status === ORDER_COMPLETE &&
        timeList[index] &&
        timeList[index].displayComplete ? (
          <Content>
            <Icon className="fas fa-badge-check" />
          </Content>
        ) : status === ORDER_REFUNDED || status === ORDER_REJECTED ? (
          <Content>
            <Icon className="fas fa-exclamation-triangle" />
          </Content>
        ) : (
          <BarContainer>
            <ProgressBar
              animate={status === ORDER_SUBMITTED || ORDER_ACCEPTED}
            />
            <ProgressBar
              animate={
                status === ORDER_ACCEPTED ||
                status === ORDER_COMPLETE ||
                status === ORDER_ON_DELIVERY
              }
            />
            <ProgressBar
              animate={
                status === ORDER_COMPLETE || status === ORDER_ON_DELIVERY
              }
            />
          </BarContainer>
        )}
      </CardContent>
      <OrderStatusMessage
        customColor={appConfig.styleConfig.orderStatusTitleColor}
        font={appConfig.styleConfig.orderStatusMessageFont}
      >
        {status === ORDER_SUBMITTED ? (
          <>Waiting for restaurant confirmation…</>
        ) : status === ORDER_ACCEPTED && delay === 0 ? (
          <>Your order is being prepared</>
        ) : status === ORDER_ACCEPTED && delay > 0 ? (
          <>
            Your order is being prepared. Please contact the store for more
            information.
          </>
        ) : status === ORDER_READY_TO_PICKUP &&
          orderType === 'pickup' &&
          timeList[index] &&
          !timeList[index].displayComplete ? (
          <>{`at ${businessName}`}</>
        ) : status === ORDER_ON_DELIVERY &&
          orderType === 'delivery' &&
          timeList[index] &&
          !timeList[index].displayComplete ? (
          <>
            {deliveryTrackingURL ? (
              <div style={{ marginTop: '25px' }}>
                <TrackButton deliveryTrackingURL={deliveryTrackingURL} />
              </div>
            ) : (
              <div>{`Your order is out for  delivery`}</div>
            )}
          </>
        ) : status === ORDER_REFUNDED || status === ORDER_REJECTED ? (
          <div>{statusMessage}</div>
        ) : status === ORDER_COMPLETE &&
          timeList[index] &&
          timeList[index].displayComplete ? (
          <div>Your order is complete!</div>
        ) : null}
      </OrderStatusMessage>
    </ContentWrapper>
  );
};
const renderTitle = (
  status,
  orderType,
  orderReadyTime,
  deliveryTrackingURL,
  displayComplete
) => {
  if (status === ORDER_SUBMITTED) {
    return <Title id="OrderStatus-Title">Order placed</Title>;
  } else if (status === ORDER_ACCEPTED && orderType === 'pickup') {
    return (
      <TitleWrapper id="OrderStatus-Title">
        <Title id="OrderStatus-Title">{orderReadyTime}</Title>
        <TitleNote>Estimated pickup</TitleNote>
      </TitleWrapper>
    );
  } else if (status === ORDER_ACCEPTED && orderType === 'delivery') {
    return (
      <TitleWrapper id="OrderStatus-Title">
        <Title id="OrderStatus-Title">{orderReadyTime}</Title>
        <TitleNote>Estimated arrival</TitleNote>
      </TitleWrapper>
    );
  } else if (
    status === ORDER_READY_TO_PICKUP &&
    orderType === 'pickup' &&
    !displayComplete
  ) {
    return <Title id="OrderStatus-Title">Ready for pickup</Title>;
  } else if (
    (status === ORDER_ON_DELIVERY && orderType === 'delivery',
    !deliveryTrackingURL && !displayComplete)
  ) {
    return (
      <TitleWrapper id="OrderStatus-Title">
        <Title id="OrderStatus-Title">{orderReadyTime}</Title>
        <TitleNote>Estimated arrival</TitleNote>
      </TitleWrapper>
    );
  } else if (
    (status === ORDER_ON_DELIVERY && orderType === 'delivery',
    deliveryTrackingURL && !displayComplete,
    ORDER_ON_DELIVERY)
  ) {
    return <Title id="OrderStatus-Title">Out for delivery</Title>;
  } else {
    return <Title id="OrderStatus-Title"></Title>;
  }
};
const OrderStatus0 = ({
  orderStatusList,
  sendReview,
  feedbackModalIsVisible,
  openFeedbackModal,
  closeFeedbackModal,
  currency,
  is_tip_enabled,
  orderHistory,
  timeList,
  setTimeList,
  isFavoriteOpen,
  setIsFavoriteOpen,
  locations,
  feedbackModalData,
  setFeedbackModalData,
  orderStatusModalData,
  setOrderStatusModalData,
  displayComplete,
  setDisplayComplete,
}) => {
  const feedbackRef = useRef();
  const isMultiple = orderStatusList.length > 1;
  useEffect(() => {
    let newTimeList = [...timeList];
    const timer = setInterval(() => {
      orderStatusList.forEach((order, index) => {
        if (order.status === 2) {
          if (timeList.length > 0) {
            var duration = moment.duration(
              moment().diff(calculateOrderReadyTime(order))
            );
            var minutes = Math.round(duration.asMinutes());
            if (minutes > 29 && !orderStatusModalData && !isFavoriteOpen) {
              newTimeList[index].displayComplete = true;
              setTimeList(newTimeList);
            } else if (
              minutes < 30 &&
              !orderStatusModalData &&
              !isFavoriteOpen &&
              newTimeList[index].displayComplete
            ) {
              newTimeList[index].displayComplete = false;
              setTimeList(newTimeList);
            }
          }
        }
      });
    }, 5000);
    return () => {
      clearInterval(timer);
    };
  }, [orderStatusList]);

  return (
    <Fragment>
      <SliderWrapper isMultiple={isMultiple} feedbackRef={feedbackRef}>
        {orderStatusList.map((orderStatus, index) => {
          const status = orderStatus.status;
          const businessName = orderStatus.business_name;
          const orderType = orderStatus.order_type;
          const orderReadyTime = calculateOrderReadyTime(orderStatus, false);
          const deliveryTrackingURL = orderStatus.delivery_tracking_url;
          const delay = orderStatus.delay;
          const statusMessage = orderStatus?.status_change_message
            ? orderStatus.status_change_message
            : ' Unfortunately, we cannot fulfill your order at this time. Your order will be refunded.';
          return (
            <OrderStatusContainer key={index}>
              <OrderStatusWrapper
                onClick={() => {
                  isOrderWithFirebaseUrl(orderHistory, orderStatus);
                  setDisplayComplete(timeList[index].displayComplete);
                  setOrderStatusModalData(orderStatus);
                }}
                isForModal={
                  isOrderWithFirebaseUrl(orderHistory, orderStatus)
                    ? false
                    : true
                }
                key={index}
              >
                {renderTitle(
                  status,
                  orderType,
                  orderReadyTime,
                  deliveryTrackingURL,
                  timeList[index] && timeList[index].displayComplete
                )}
                <OrderStatusBody
                  orderType={orderType}
                  status={status}
                  businessName={businessName}
                  orderReadyTime={orderReadyTime}
                  deliveryTrackingURL={deliveryTrackingURL}
                  delay={delay}
                  index={index}
                  timeList={timeList}
                  statusMessage={statusMessage}
                />
              </OrderStatusWrapper>
              {orderStatusList?.length > 1 && (
                <OrderPlacedTimeWrapper>
                  {`Placed on: ${moment(orderStatus?.order_created_at).format(
                    'MMMM Do, hh:mm A'
                  )}`}
                </OrderPlacedTimeWrapper>
              )}
              {appConfig.appSource !== 'madradish' &&
              (status === ORDER_COMPLETE || status === ORDER_ON_DELIVERY) ? (
                <FeedbackWrapper ref={feedbackRef}>
                  <RateTextWrapper>
                    <i
                      className="fal fa-user-headset"
                      style={{ lineHeight: '40px' }}
                    ></i>
                    <span style={{ display: 'inline-block' }}>
                      Tell us how we did today. Your feedback helps us improve.
                    </span>
                  </RateTextWrapper>
                  <ButtonWrapper>
                    <Button
                      id="OrderStatus-FeedbackButton"
                      onClick={() => {
                        setFeedbackModalData(orderStatus);
                        setIsFavoriteOpen(true);
                      }}
                    >
                      Rate your order
                    </Button>
                  </ButtonWrapper>
                </FeedbackWrapper>
              ) : null}
            </OrderStatusContainer>
          );
        })}
      </SliderWrapper>
      <FeedbackModal
        feedbackModalData={feedbackModalData}
        setFeedbackModalData={setFeedbackModalData}
        sendReview={sendReview}
        feedbackModalIsVisible={feedbackModalIsVisible}
        openFeedbackModal={openFeedbackModal}
        closeFeedbackModal={closeFeedbackModal}
        isFavoriteOpen={isFavoriteOpen}
        setIsFavoriteOpen={setIsFavoriteOpen}
      />
      <OrderStatusModal3
        orderStatusModalData={orderStatusModalData}
        setOrderStatusModalData={setOrderStatusModalData}
        isMultiple={isMultiple}
        currency={currency}
        is_tip_enabled={is_tip_enabled}
        displayComplete={displayComplete}
        locations={locations}
      />
    </Fragment>
  );
};

export default memo(OrderStatus0);
